import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatMoney2, formatNumber } from "../lib/api";
import { Sliders, Loader2, Info } from "lucide-react";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import { SubirArriba } from "../components/SubirArriba";

const ABCD_INFO = {
  AA: "Min = ventas diaria × 2 · Max = ventas diaria × 10",
  A:  "Min = ventas diaria × 2 · Max = ventas diaria × 7",
  BB: "Min = 1 · Max = 1",
  B:  "Min = 1 · Max = 1",
  CC: "Min = 1 · Max = 1",
  C:  "Min = 0 · Max = 0",
  D:  "Min = 0 · Max = 0",
};

const ABCD_RITMO = {
  AA: { ritmo: "Se vende cada día", uds: "más de 365 uds/año" },
  A:  { ritmo: "Se vende cada semana", uds: "52 a 365 uds/año" },
  BB: { ritmo: "Se vende cada decena", uds: "35 a 51 uds/año" },
  B:  { ritmo: "Se vende cada mes", uds: "12 a 34 uds/año" },
  CC: { ritmo: "Se vende cada trimestre", uds: "4 a 11 uds/año" },
  C:  { ritmo: "Se vende alguna vez al año", uds: "1 a 3 uds/año" },
  D:  { ritmo: "Sin ventas", uds: "0 uds/año" },
};

export default function MinMax() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studyMeta, setStudyMeta] = useState(null);
  const [abcdFilter, setAbcdFilter] = useState("");
  const [diffFilter, setDiffFilter] = useState("ALL"); // ALL | DIFF | MISSING
  const [segFilter, setSegFilter] = useState("");
  const [famFilter, setFamFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: studies } = await api.get("/studies");
        if (!studies?.length) { setLoading(false); return; }
        const latest = studies[0];
        setStudyMeta(latest);
        const { data: d } = await api.get(`/studies/${latest.id}/min-max`, { params: { family_filter: "" } });
        setData(d);
        setFamFilter("__esp");
      } finally { setLoading(false); }
    })();
  }, []);

  const familias = [...new Set((data?.items || []).filter((p) => !segFilter || p.segmento === segFilter).map((p) => p.familia).filter(Boolean))].sort();
  const filtered = (data?.items || []).filter((p) => {
    if (segFilter && p.segmento !== segFilter) return false;
    if (famFilter === "__esp" ? !(p.familia || "").toLowerCase().includes("especialidades") : famFilter && p.familia !== famFilter) return false;
    if (abcdFilter && p.abcd !== abcdFilter) return false;
    if (diffFilter === "DIFF") {
      return (p.current_min != null && p.current_min !== p.suggested_min)
          || (p.current_max != null && p.current_max !== p.suggested_max);
    }
    if (diffFilter === "MISSING") {
      return p.current_min == null || p.current_max == null;
    }
    return true;
  });
  const sort = useSortable(filtered, "uds_vendidas", "desc");

  if (loading) return <div className="p-8 text-slate-500 flex gap-2"><Loader2 className="animate-spin" size={16} /> Cargando…</div>;
  if (!studyMeta) return <div className="p-8 text-slate-500">No tienes ningún estudio cargado. <Link to="/upload" className="text-blue-700 font-semibold">Sube uno primero</Link>.</div>;

  return (
    <div className="p-8 fade-in" data-testid="min-max-page">
      <SubirArriba />
      <img src="/img/farmanaliza-minmax.jpg?v=2" alt="Farmanaliza · Mínimos y máximos: reposición, rotación y control de stock"
           className="w-full max-w-xl rounded-2xl shadow-sm h-auto mb-4" data-testid="min-max-banner" />
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{famFilter === "__esp" ? "Especialidades" : famFilter || (segFilter ? (segFilter === "farmacia" ? "Farmacia" : "Parafarmacia") : "Todas las familias")} · {formatNumber(filtered.length)} productos</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <Sliders size={28} className="text-slate-400" /> Mínimos / Máximos
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Los valores en <span className="text-blue-700 font-bold">azul</span> son los sugeridos por Farmanaliza basados en la regla por clasificación ABCD.
            Los valores en negro son los que tienes cargados en tu Excel para comparar.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          Estudio: <span className="font-semibold">{studyMeta.name}</span> · {studyMeta.period_days}d
        </div>
      </div>

      {/* Rules card */}
      <div className="card p-5 mt-4 mb-4 bg-blue-50/40 border-blue-200" data-testid="rules-card">
        <div className="flex items-center gap-2 mb-2 text-blue-900">
          <Info size={14} /> <span className="font-semibold text-sm">Regla de cálculo</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-2 text-xs">
          {Object.entries(ABCD_INFO).map(([k, v]) => (
            <div key={k} className="flex gap-2 items-start" data-testid={`minmax-rule-${k}`}>
              <span className={`abcd-pill abcd-${k}`}>{k}</span>
              <div>
                <div className="font-semibold text-slate-900">{ABCD_RITMO[k].ritmo} <span className="font-mono text-slate-500 font-normal">· {ABCD_RITMO[k].uds}</span></div>
                <div className="text-slate-600">{v}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-lg p-0.5" data-testid="minmax-segment">
          {[["", "Todo"], ["farmacia", "Farmacia"], ["parafarmacia", "Parafarmacia"]].map(([k, l]) => (
            <button key={k || "all"} onClick={() => { setSegFilter(k); setFamFilter(""); }} data-testid={`minmax-seg-${k || "all"}`}
                    className={"px-3 py-1.5 text-xs font-semibold rounded-md transition-colors " + (segFilter === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
        <select value={famFilter} onChange={(e) => setFamFilter(e.target.value)} className="input" style={{ width: 240 }} data-testid="minmax-familia-filter">
          <option value="">Familia: todas</option>
          <option value="__esp">Especialidades (todas)</option>
          {familias.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={abcdFilter} onChange={(e) => setAbcdFilter(e.target.value)} className="input" style={{ width: 180 }} data-testid="abcd-filter">
          <option value="">Clase: todas</option>
          {Object.keys(ABCD_INFO).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={diffFilter} onChange={(e) => setDiffFilter(e.target.value)} className="input" style={{ width: 240 }} data-testid="diff-filter">
          <option value="ALL">Mostrar todos</option>
          <option value="DIFF">Solo con diferencias (recomendados)</option>
          <option value="MISSING">Sin Min/Max cargado en mi Excel</option>
        </select>
        <div className="ml-auto">
          <ExportButtons
            filename={`min-max-especialidades-${studyMeta.name}`}
            title={`Mínimos / Máximos · ${famFilter === "__esp" ? "Especialidades" : famFilter || "Todas las familias"}`}
            subtitle={`Estudio: ${studyMeta.name} · ${studyMeta.period_days} días · ${sort.sortedRows.length} productos`}
            sheetName="Min-Max"
            orientation="landscape"
            columns={[
              { header: "CN", key: "cn" },
              { header: "Descripción", key: "descripcion" },
              { header: "Familia", key: "familia" },
              { header: "Laboratorio", key: "laboratorio" },
              { header: "ABCD", key: "abcd" },
              { header: "Stock", key: "stock", align: "right" },
              { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
              { header: "Venta diaria", key: "venta_diaria", format: (v) => (v || 0).toFixed(2), align: "right" },
              { header: "Min actual", key: "current_min", align: "right" },
              { header: "Min sugerido", key: "suggested_min", align: "right" },
              { header: "Max actual", key: "current_max", align: "right" },
              { header: "Max sugerido", key: "suggested_max", align: "right" },
              { header: "Última compra", key: "u_entrada", format: (v) => v ? new Date(v).toLocaleDateString("es-ES") : "" },
            ]}
            rows={sort.sortedRows}
          />
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="dense" data-testid="min-max-table">
          <thead>
            <tr>
              <th><SortHeader label="CN" k="cn" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Descripción" k="descripcion" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="ABCD" k="abcd" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Stock" k="stock" sort={sort} num /></th>
              <th className="num"><SortHeader label="Uds vendidas" k="uds_vendidas" sort={sort} num /></th>
              <th className="num"><SortHeader label="V. diaria" k="venta_diaria" sort={sort} num /></th>
              <th className="num"><SortHeader label="Min actual" k="current_min" sort={sort} num /></th>
              <th className="num"><SortHeader label="Min sugerido" k="suggested_min" sort={sort} num /></th>
              <th className="num"><SortHeader label="Max actual" k="current_max" sort={sort} num /></th>
              <th className="num"><SortHeader label="Max sugerido" k="suggested_max" sort={sort} num /></th>
              <th><SortHeader label="Última compra" k="u_entrada" sort={sort} defaultDir="desc" /></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.length === 0 && (
              <tr><td colSpan={12} className="text-center py-8 text-slate-500">Sin resultados con los filtros aplicados.</td></tr>
            )}
            {sort.sortedRows.slice(0, 500).map((p, i) => {
              const minMatch = p.current_min != null && p.current_min === p.suggested_min;
              const maxMatch = p.current_max != null && p.current_max === p.suggested_max;
              return (
                <tr key={`mm-${i}-${p.cn}`} data-testid={`min-max-row-${p.cn}`}>
                  <td className="font-mono">{p.cn}</td>
                  <td>{p.descripcion}</td>
                  <td className="text-xs">{p.laboratorio}</td>
                  <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                  <td className="num">{formatNumber(p.stock)}</td>
                  <td className="num">{formatNumber(p.uds_vendidas)}</td>
                  <td className="num text-slate-500">{p.venta_diaria.toFixed(2)}</td>
                  <td className="num">{p.current_min == null ? <span className="text-slate-300">—</span> : p.current_min}</td>
                  <td className={"num font-bold text-blue-700 " + (minMatch ? "opacity-50" : "")}>
                    {p.suggested_min}
                  </td>
                  <td className="num">{p.current_max == null ? <span className="text-slate-300">—</span> : p.current_max}</td>
                  <td className={"num font-bold text-blue-700 " + (maxMatch ? "opacity-50" : "")}>
                    {p.suggested_max}
                  </td>
                  <td className="text-xs font-mono whitespace-nowrap">{p.u_entrada ? new Date(p.u_entrada).toLocaleDateString("es-ES") : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {sort.sortedRows.length > 500 && (
          <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
            Mostrando los primeros 500 de {sort.sortedRows.length} productos. Exporta a CSV para verlos todos.
          </div>
        )}
      </div>
    </div>
  );
}
